import { Button } from '@shared/components/shadcn';
import type { ComponentProps } from 'react';
import type { Option } from '../form-controls/types';
import { HorizontalScrollContainer } from '../HorizontalScrollContainer/HorizontalScrollContainer';
import { PulseNotification } from '../lotteries/PulseNotification/PulseNotification';

type ContextualNavigationProps<T extends string = string> = Omit<ComponentProps<'div'>, 'onChange'> &
  Pick<ComponentProps<typeof Button>, 'size'> & {
    options: Option<T>[];
    value: Option<T>;
    onChange: (option: Option<T>) => void;
  };

/**
 * ContextualNavigation component is a horizontal scroll container that displays a list of options as buttons.
 * Extra padding is applied to prevent overflow clipping of notifications and focus-ring,
 * compensated with negative margin to maintain visual position.
 *
 * @param options - The options to display
 * @param value - The selected option
 * @param onChange - The function to call when an option is selected
 * @param size - The size of the button
 * @param props - The props to pass to the root div element
 */
function ContextualNavigation<T extends string>({ options, value, onChange, size, ...props }: ContextualNavigationProps<T>) {
  return (
    <div className="-my-3" {...props}>
      <HorizontalScrollContainer role="listbox" className="py-3">
        {options.map(option => (
          <Button
            role="option"
            aria-selected={option.value === value.value ? true : undefined}
            key={option.value}
            onClick={() => onChange(option)}
            size={size}
            variant={option.value === value.value ? 'primary' : 'secondary'}
            className="w-full relative"
            data-track-name={option.value}
          >
            {option.label}
            {option.showNotification && <PulseNotification className="absolute -top-1 right-2.5" />}
          </Button>
        ))}
      </HorizontalScrollContainer>
    </div>
  );
}

export { ContextualNavigation };
